// src/pages/login.jsx — FIXED
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Alert } from '../components/ui';
import Logo from "../assets/logo.png"

export default function LoginPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('phone');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const user = await login({ identifier: identifier.trim(), password: password.trim() });
      if (user.role === 'admin') navigate('/dashboard', { replace: true });
      else if (user.role === 'driver') navigate('/driver/dashboard', { replace: true });
      else if (user.role === 'operator') navigate('/operator/dashboard', { replace: true });
      else navigate('/main', { replace: true });
    } catch (err) {
      if (err.message === 'USER_NOT_VERIFIED') {
        navigate('/verify');
      } else if (err.message === 'USER_BLOCKED') {
        setError(t('apiErrors.USER_BLOCKED') || 'Hisobingiz bloklangan');
      } else if (err.message === 'INVALID_CREDENTIALS') {
        setError(t('apiErrors.INVALID_CREDENTIALS') || 'Login yoki parol noto\'g\'ri');
      } else {
        setError(t('apiErrors.INTERNAL_ERROR') || 'Xatolik yuz berdi');
      }
    }
  };

  const switchTab = (tCode) => {
    setTab(tCode);
    setIdentifier('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[var(--bg-app)] bg-gradient-to-br from-[var(--bg-app)] via-[var(--bg-subtle)] to-emerald-500/[0.08] flex items-center justify-center px-5 py-8 overflow-hidden relative">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[320px] w-[320px] rounded-full bg-[#1e9f6b]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 h-[280px] w-[280px] rounded-full bg-sky-400/8 blur-3xl pointer-events-none" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[380px] relative z-10"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="flex flex-col items-center gap-4 mb-10"
        >
          <div className="relative group">
            <div className="absolute inset-0" />
            <div className="relative">
              <img src={Logo} alt="SmartGaz" className="w-[84px] h-[84px] object-contain" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="bg-[var(--bg-surface)] rounded-[var(--radius-2xl)] border border-[var(--border)] shadow-[var(--shadow-2xl)] p-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[40px] pointer-events-none" />

          {/* Tabs */}
          <div className="flex gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-1.5 mb-8 border border-slate-100 dark:border-slate-800/50 shadow-sm">
            {[
              { key: 'phone', label: t('auth.phone') || 'Telefon', icon: 'phone' },
              { key: 'email', label: t('auth.email') || 'Email', icon: 'mail' },
            ].map((tabItem) => (
              <button
                key={tabItem.key}
                onClick={() => switchTab(tabItem.key)}
                className={`flex-1 flex items-center justify-center gap-2 h-12 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${
                  tab === tabItem.key
                    ? 'bg-white dark:bg-slate-900 text-[#1e9f6b] shadow-md shadow-slate-200/50 dark:shadow-none'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <span className="material-symbols-outlined text-[18px]">{tabItem.icon}</span>
                {tabItem.label}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, x: tab === 'phone' ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: tab === 'phone' ? 20 : -20 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <Input
                  label={tab === 'phone' ? (t('auth.phone') || 'Telefon') : (t('auth.email') || 'Email')}
                  placeholder={tab === 'phone' ? '+998 90 123 45 67' : 'email@gmail.com'}
                  type={tab === 'phone' ? 'tel' : 'email'}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  icon={tab === 'phone' ? 'phone' : 'mail'}
                  autoComplete={tab === 'phone' ? 'tel' : 'email'}
                  required
                  className="h-14 font-black italic shadow-sm"
                />
              </motion.div>
            </AnimatePresence>

            <Input
              label={t('auth.password') || 'Parol'}
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon="lock"
              rightIcon={showPwd ? 'visibility_off' : 'visibility'}
              onRightIconClick={() => setShowPwd(!showPwd)}
              autoComplete="current-password"
              required
              className="h-14 font-black tracking-widest italic shadow-sm"
            />

            <Alert message={error} type="error" />

            <Button type="submit" size="xl" loading={isLoading}
              className="h-16 font-black uppercase tracking-[0.2em] text-sm shadow-xl shadow-[#1e9f6b]/30 active:scale-[0.98]">
              {t('auth.submit') || 'Kirish'}
            </Button>

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
              <span className="flex-shrink-0 mx-4 text-slate-400 text-[10px] font-black tracking-widest uppercase">{t('auth.orDivider') || 'yoki'}</span>
              <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
            </div>

            <button
              type="button"
              onClick={() => window.location.href = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1') + '/auth/google'}
              className="flex items-center justify-center gap-3 w-full h-14 bg-white dark:bg-slate-800 text-slate-700 dark:text-white rounded-xl border border-slate-200 dark:border-slate-700 font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
            >
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
              <span>{t('auth.googleLogin') || 'Google orqali kirish'}</span>
            </button>

            <div className="flex items-center justify-between gap-3 pt-2">
              <Link to="/register"
                className="text-[10px] font-black uppercase tracking-widest text-[#1e9f6b] hover:text-[#0d7a52] transition-colors decoration-2 underline-offset-4 hover:underline">
                {t('auth.noAccount') || "Ro'yxatdan o'tish"}
              </Link>
              <Link to="/reset-password"
                className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors">
                {t('auth.resetPassword') || 'Parolni tiklash?'}
              </Link>
            </div>
          </form>
        </motion.div>

        <p className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-10 opacity-60">
          © {new Date().getFullYear()} SmartGaz
        </p>
      </motion.div>
    </div>
  );
}
